import { Link, useParams } from "react-router-dom";
import { useEffect } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import SEOHead from "@/components/feature/SEOHead";
import SchemaOrg from "@/components/feature/SchemaOrg";
import { movies, categories } from "@/mocks/movies";
import type { Movie } from "@/mocks/movies";

function MovieCard({ movie }: { movie: Movie }) {
  const categoryName = categories.find((c) => c.slug === movie.category)?.name || movie.category;
  return (
    <Link
      to={`/article/${movie.id}`}
      className="group block bg-background-50 rounded-xl overflow-hidden border border-background-200 hover:border-accent-300 transition-all duration-300 cursor-pointer"
    >
      <div className="relative w-full aspect-[3/4] overflow-hidden">
        <img
          src={movie.posterUrl}
          alt={movie.title}
          title={`${movie.title} - 屁屁影院在线观看`}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent"></div>
        <div className="absolute top-3 left-3 px-2 py-1 bg-accent-500 text-background-50 text-xs font-medium rounded-md">
          {categoryName}
        </div>
        <div className="absolute top-3 right-3 flex items-center gap-1 px-2 py-1 bg-black/60 text-amber-400 text-xs font-semibold rounded-md">
          <i className="ri-star-fill text-[10px]"></i>
          {movie.rating}
        </div>
        <div className="absolute bottom-3 left-3 right-3">
          <h3 className="text-sm font-semibold text-white truncate font-heading">
            {movie.title}
          </h3>
          <p className="text-[11px] text-white/70 mt-1 truncate">
            {movie.year}
          </p>
        </div>
      </div>
    </Link>
  );
}

export default function Article() {
  const { id } = useParams<{ id: string }>();
  const movieId = parseInt(id || "1", 10);
  const movie = movies.find((m) => m.id === movieId);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [id]);

  if (!movie) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="max-w-[1400px] mx-auto px-4 md:px-6 pt-24 pb-14 text-center">
          <div className="w-20 h-20 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-4">
            <i className="ri-emotion-sad-line text-3xl"></i>
          </div>
          <h1 className="text-xl font-bold text-foreground-950 mb-2">未找到该影视资源</h1>
          <p className="text-foreground-600 mb-6">该内容可能已下架或链接有误</p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 rounded-lg hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-home-4-line"></i>
            返回首页
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const categoryName = categories.find((c) => c.slug === movie.category)?.name || movie.category;
  const relatedMovies = movies
    .filter((m) => m.id !== movie.id && (m.category === movie.category || m.genre.some((g) => movie.genre.includes(g))))
    .slice(0, 6);

  const title = `屁屁影院 - ${movie.title} 在线观看`;
  const desc = `${movie.title}，${movie.year}年${movie.region}${categoryName}，导演${movie.director}，主演${movie.actors.join("、")}。${movie.description.slice(0, 80)}... 屁屁影院免费在线观看。`;
  const keywords = `屁屁影院,${movie.title},${movie.title}在线观看,${movie.title}${categoryName},${movie.genre.join(",")},${movie.actors.slice(0, 2).join(",")}`;

  return (
    <>
      <SEOHead
        title={title}
        description={desc}
        keywords={keywords}
        canonicalUrl={`https://www.pipicinema.com/article/${movie.id}`}
        ogType="article"
      />
      <SchemaOrg
        type="BreadcrumbList"
        data={{
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "首页", item: "https://www.pipicinema.com/" },
            { "@type": "ListItem", position: 2, name: categoryName, item: `https://www.pipicinema.com/category/${movie.category}` },
            { "@type": "ListItem", position: 3, name: movie.title },
          ],
        }}
      />
      <SchemaOrg
        type="Article"
        data={{
          headline: movie.title,
          description: movie.description,
          author: { "@type": "Person", name: movie.director },
          datePublished: movie.releaseDate,
          image: movie.bannerUrl,
          publisher: { "@type": "Organization", name: "屁屁影院" },
        }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="max-w-[1400px] mx-auto px-4 md:px-6 pt-24 pb-14">
          <Breadcrumb
            items={[
              { label: "首页", path: "/" },
              { label: categoryName, path: `/category/${movie.category}` },
              { label: movie.title },
            ]}
          />

          {/* Banner */}
          <div className="relative w-full h-[280px] md:h-[450px] rounded-2xl overflow-hidden mb-8">
            <img
              src={movie.bannerUrl}
              alt={movie.title}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-black/20"></div>
          </div>

          {/* Main Content */}
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Poster Sidebar */}
            <div className="flex-shrink-0 w-full lg:w-[280px]">
              <div className="sticky top-24">
                <div className="w-full aspect-[3/4] rounded-xl overflow-hidden border border-background-200">
                  <img
                    src={movie.posterUrl}
                    alt={movie.title}
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>

            {/* Info */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 mb-3 flex-wrap">
                <span className="px-3 py-1 bg-accent-100 text-accent-700 text-xs font-medium rounded-full">
                  {categoryName}
                </span>
                {movie.genre.map((g) => (
                  <span
                    key={g}
                    className="px-3 py-1 bg-secondary-100 text-secondary-700 text-xs font-medium rounded-full"
                  >
                    {g}
                  </span>
                ))}
              </div>

              <h1 className="text-2xl md:text-4xl font-bold text-foreground-950 mb-4 font-heading">
                {movie.title}
              </h1>

              <div className="flex items-center gap-4 mb-6 flex-wrap">
                <div className="flex items-center gap-1.5">
                  <i className="ri-star-fill text-amber-400"></i>
                  <span className="text-lg font-bold text-foreground-950">{movie.rating}</span>
                  <span className="text-sm text-foreground-500">/ 10</span>
                </div>
                <span className="text-foreground-400">|</span>
                <span className="text-sm text-foreground-600">{movie.year}</span>
                <span className="text-foreground-400">|</span>
                <span className="text-sm text-foreground-600">{movie.region}</span>
                <span className="text-foreground-400">|</span>
                <span className="text-sm text-foreground-600">{movie.duration}</span>
              </div>

              {/* Meta Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6 p-5 bg-background-100/70 rounded-xl">
                <div>
                  <span className="text-xs text-foreground-500">导演</span>
                  <p className="text-sm text-foreground-900 font-medium mt-0.5">
                    {movie.director}
                  </p>
                </div>
                <div>
                  <span className="text-xs text-foreground-500">主演</span>
                  <p className="text-sm text-foreground-900 font-medium mt-0.5">
                    {movie.actors.join(" / ")}
                  </p>
                </div>
                <div>
                  <span className="text-xs text-foreground-500">类型</span>
                  <p className="text-sm text-foreground-900 font-medium mt-0.5">
                    {movie.genre.join(" / ")}
                  </p>
                </div>
                <div>
                  <span className="text-xs text-foreground-500">上映日期</span>
                  <p className="text-sm text-foreground-900 font-medium mt-0.5">
                    {movie.releaseDate}
                  </p>
                </div>
              </div>

              {/* Description */}
              <div className="mb-8">
                <h2 className="text-lg font-semibold text-foreground-950 mb-3 font-heading">
                  剧情简介
                </h2>
                <p className="text-sm text-foreground-700 leading-relaxed">
                  {movie.description}
                </p>
              </div>

              {/* Watch CTA */}
              <div className="flex items-center gap-4 flex-wrap">
                <button className="inline-flex items-center gap-2 px-8 py-3.5 bg-primary-500 text-background-50 font-medium rounded-lg hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap">
                  <i className="ri-play-circle-fill text-xl"></i>
                  立即播放
                </button>
                <button className="inline-flex items-center gap-2 px-8 py-3.5 bg-secondary-500 text-background-50 font-medium rounded-lg hover:bg-secondary-600 transition-colors cursor-pointer whitespace-nowrap">
                  <i className="ri-heart-line text-lg"></i>
                  收藏
                </button>
              </div>
            </div>
          </div>

          {/* Related Movies */}
          {relatedMovies.length > 0 && (
            <section className="mt-14">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl md:text-2xl font-bold text-foreground-950 font-heading">
                  <i className="ri-links-line text-accent-500 mr-2"></i>
                  相关推荐
                </h2>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-5">
                {relatedMovies.map((m) => (
                  <MovieCard key={m.id} movie={m} />
                ))}
              </div>
            </section>
          )}
        </main>

        <Footer />
      </div>
    </>
  );
}